import React, { useState, useEffect } from 'react';
import { Project, ProjectStatus, ProjectPriority, ProjectType, UserData, Artist, canModifyProject } from '../types';
import { ICONS } from '../constants';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import Select from './ui/Select';
import ConfirmationModal from './ui/ConfirmationModal';
import ProjectArtistAssignment from './ProjectArtistAssignment';
import { useTranslation } from '../hooks/useTranslation';
import { API_BASE_URL, API_CONFIG } from '../config';

interface ProjectManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  projectToEdit?: Project | null;
  onCreateProject: (project: Omit<Project, 'id'>) => Promise<Project>;
  onUpdateProject: (project: Project) => Promise<void>;
  onDeleteProject: (projectId: string) => void;
  currentRole: string | null;
  currentUserId?: string;
}

// Helper: Check if user can view sensitive data
const canViewSensitive = (role: string | null, project: Project, userId?: string): boolean => {
  if (!role) return false;
  if (role === 'Super Admin' || role === 'Admin' || role === 'Producer') return true;
  if (project.createdBy && userId && project.createdBy === userId) return true;
  return false;
};

/** Profile types eligible for the project Coordinator slot (includes Admin / Super Admin → admin). */
const COORDINATOR_ASSIGNABLE_TYPES = ['coordinator', 'supervisor', 'admin', 'producer'] as const;

/** Profile types eligible for the project Producer slot (includes Admin / Super Admin → admin). */
const PRODUCER_ASSIGNABLE_TYPES = ['producer', 'sales', 'admin', 'coordinator'] as const;

// Форматирование даты для отображения
const formatDate = (dateString: string): string => {
  if (!dateString) return 'Не указано';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('ru-RU');
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Неверная дата';
    return date.toLocaleDateString('ru-RU');
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Ошибка даты';
  }
};

// Нормализация даты в формат YYYY-MM-DD
const normalizeDate = (dateString: string): string => {
  if (!dateString) return '';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error normalizing date:', dateString, error);
    return '';
  }
};

interface ManagementUser {
  id: string;
  username: string;
  role: string;
  profileId: string;
  profileType: string;
}

interface AssignableUser {
  id: string;
  username: string;
  email?: string;
  role: string;
}

const resolveAssignmentSelection = (
  profileId: string | null | undefined,
  userId: string | null | undefined,
  management: ManagementUser[]
): string => {
  if (profileId) return profileId;
  if (userId) {
    const mgmt = management.find((m) => m.id === userId);
    return mgmt?.profileId || userId;
  }
  return '';
};

const injectMissingAssignee = (
  list: AssignableUser[],
  profileId: string | null | undefined,
  userId: string | null | undefined,
  displayName: string | null | undefined,
  management: ManagementUser[]
): AssignableUser[] => {
  const resolvedId = resolveAssignmentSelection(profileId, userId, management);
  if (!resolvedId || list.some((u) => u.id === resolvedId)) {
    return list;
  }
  const mgmt = management.find((m) => m.profileId === resolvedId || m.id === resolvedId);
  const name = displayName || mgmt?.username || 'Assigned';
  const role = mgmt?.profileType || 'admin';
  return [...list, { id: resolvedId, username: name, email: '', role }];
};

const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({ 
  isOpen, 
  onClose, 
  projects,
  projectToEdit,
  onCreateProject, 
  onUpdateProject, 
  onDeleteProject,
  currentRole,
  currentUserId
}) => {
  const { t } = useTranslation();
  
  // Basic fields
  const [projectName, setProjectName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  
  // New fields
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('Planning');
  const [budget, setBudget] = useState<string>('');
  const [currency, setCurrency] = useState<string>('USD'); // Currency for budget
  const [taxRate, setTaxRate] = useState<string>('0'); // Tax Rate %
  const [prepaymentType, setPrepaymentType] = useState<'PERCENT' | 'FIXED' | ''>('');
  const [prepaymentValue, setPrepaymentValue] = useState<string>('');
  const [client, setClient] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [priority, setPriority] = useState<ProjectPriority>('Medium');
  const [projectType, setProjectType] = useState<ProjectType | ''>('');
  const [tags, setTags] = useState<string>(''); // comma-separated
  
  // New fields for image and assignments
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [producerId, setProducerId] = useState<string>('');
  const [managerId, setManagerId] = useState<string>('');
  const [coordinatorId, setCoordinatorId] = useState<string>('');
  const [finderId, setFinderId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [progress, setProgress] = useState<number>(10);
  const [magicBoxId, setMagicBoxId] = useState<string | null>(null);
  
  // Users list for assignment
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [managementUsers, setManagementUsers] = useState<ManagementUser[]>([]);
  const [clients, setClients] = useState<UserData[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  
  const [projectToEditId, setProjectToEditId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  /** Create wizard: 1 = project fields, 2 = artists */
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    project: Project | null;
  }>({
    isOpen: false,
    project: null
  });
  
  const isEditing = projectToEditId !== null;
  const isCreateWizard = !projectToEdit && !isEditing;
  
  // Super Admin, Admin, and Producer can assign producer / PM / coordinator / finder / client
  const canEditSensitive =
    currentRole === 'Super Admin' || currentRole === 'Admin' || currentRole === 'Producer';
  
  // Fetch users for assignment
  useEffect(() => {
    if (isOpen && canEditSensitive) {
      fetchUsers();
      fetchClients();
    }
  }, [isOpen, canEditSensitive]);

  // Load project data when editing
  useEffect(() => {
    if (projectToEdit && isOpen) {
      handleEditClick(projectToEdit);
    } else if (!projectToEdit && isOpen) {
      resetForm();
    }
  }, [projectToEdit, isOpen]);

  // Автоматически обновляем progress при изменении статуса (если проект не в MagicBox)
  useEffect(() => {
    if (!magicBoxId) {
      switch (status) {
        case 'Planning':
          setProgress(10);
          break;
        case 'In Progress':
          setProgress(50);
          break;
        case 'Completed':
          setProgress(100);
          break;
        case 'On Hold':
        case 'Cancelled':
          // Сохраняем текущий progress
          break;
      }
    }
  }, [status, magicBoxId]); // progress не в зависимостях чтобы не было цикла
  
  const fetchProfilesByType = async (profileType: string): Promise<Artist[]> => {
    const token = localStorage.getItem('spellbook_token');
    const response = await fetch(
      `${API_BASE_URL}/api/v1/artists?profileType=${encodeURIComponent(profileType)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : data.artists ?? [];
  };

  const fetchManagementUsers = async (): Promise<ManagementUser[]> => {
    const token = localStorage.getItem('spellbook_token');
    const response = await fetch(API_CONFIG.AUTH.MANAGEMENT_USERS, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const [producers, supervisors, coordinators, sales, finders, admins, management] =
        await Promise.all([
          fetchProfilesByType('producer'),
          fetchProfilesByType('supervisor'),
          fetchProfilesByType('coordinator'),
          fetchProfilesByType('sales'),
          fetchProfilesByType('finder'),
          fetchProfilesByType('admin'),
          fetchManagementUsers(),
        ]);
      setManagementUsers(management);

      const merged = [...producers, ...supervisors, ...coordinators, ...sales, ...finders, ...admins];
      const byId = new Map<string, AssignableUser>();
      merged.forEach((p) => {
        byId.set(p.id, {
          id: p.id,
          username: p.name,
          email: p.email,
          role: p.profileType ?? 'producer',
        });
      });
      // Prefer live user role → profileType so Admin/Coordinator role changes show in dropdowns
      // even if artists.profile_type is stale.
      management.forEach((m) => {
        const existing = byId.get(m.profileId);
        byId.set(m.profileId, {
          id: m.profileId,
          username: m.username || existing?.username || '',
          email: existing?.email || '',
          role: m.profileType || existing?.role || 'admin',
        });
      });
      setUsers(Array.from(byId.values()));
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Resolve legacy user UUIDs to profile IDs and inject missing assignees when editing
  useEffect(() => {
    if (!isOpen || !projectToEditId || loadingUsers) return;

    const project = projects.find((p) => p.id === projectToEditId) || projectToEdit;
    if (!project) return;

    setProducerId(
      resolveAssignmentSelection(project.producerProfileId, project.producerId, managementUsers)
    );
    setManagerId(
      resolveAssignmentSelection(project.managerProfileId, project.managerId, managementUsers)
    );
    setCoordinatorId(
      resolveAssignmentSelection(
        project.coordinatorProfileId,
        project.coordinatorId,
        managementUsers
      )
    );
    setFinderId(
      resolveAssignmentSelection(project.finderProfileId, project.finderId, managementUsers)
    );

    setUsers((prev) => {
      let next = prev;
      next = injectMissingAssignee(
        next,
        project.producerProfileId,
        project.producerId,
        project.producerName,
        managementUsers
      );
      next = injectMissingAssignee(
        next,
        project.managerProfileId,
        project.managerId,
        project.managerName,
        managementUsers
      );
      next = injectMissingAssignee(
        next,
        project.coordinatorProfileId,
        project.coordinatorId,
        project.coordinatorName,
        managementUsers
      );
      next = injectMissingAssignee(
        next,
        project.finderProfileId,
        project.finderId,
        project.finderName,
        managementUsers
      );
      return next;
    });
  }, [isOpen, projectToEditId, loadingUsers, managementUsers, projects, projectToEdit]);
  
  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const clients = await fetchProfilesByType('client');
      setClients(
        clients.map((p) => ({
          id: p.id,
          username: p.name,
          email: p.email,
          role: 'Client',
        })) as UserData[]
      );
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const resetForm = () => {
    setProjectName('');
    setProjectStartDate('');
    setProjectEndDate('');
    setDescription('');
    setStatus('Planning');
    setBudget('');
    setCurrency('USD'); // Reset to default currency
    setTaxRate('0');
    setPrepaymentType('');
    setPrepaymentValue('');
    setClient('');
    setColor('#6366f1');
    setPriority('Medium');
    setProjectType('');
    setTags('');
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setProducerId('');
    setManagerId('');
    setCoordinatorId('');
    setFinderId('');
    setClientId('');
    setProgress(10);
    setMagicBoxId(null);
    setProjectToEditId(null);
    setFormError('');
    setWizardStep(1);
    setCreatedProjectId(null);
  };
  
  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setFormError('Пожалуйста, выберите изображение');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormError('Размер изображения не должен превышать 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setFormError('');
    }
  };
  
  // Remove image
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
  };
  
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Upload project image to server
  const uploadProjectImage = async (projectId: string, file: File): Promise<string | null> => {
    try {
      const token = localStorage.getItem('spellbook_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_CONFIG.PROJECTS.LIST}/${projectId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error('Error uploading project image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (projectName.trim() && projectStartDate && projectEndDate) {
      if (new Date(projectStartDate) > new Date(projectEndDate)) {
        setFormError('Дата начала не может быть позже даты окончания');
        return;
      }
      
      const projectData: any = {
        name: projectName.trim(),
        startDate: projectStartDate,
        endDate: projectEndDate,
        description: description.trim() || undefined,
        status: status || 'Planning',
        currency, // Currency is available for all roles
        client: client.trim() || undefined,
        color: color || '#6366f1',
        priority: priority || 'Medium',
        projectType: projectType || undefined,
        tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        imageUrl: imageUrl || undefined
      };
      
      // Progress автоматически рассчитывается на основе статуса (если проект не в MagicBox)
      // Для проектов в MagicBox progress берется из API MagicBox, поэтому не отправляем его
      if (!magicBoxId) {
        // Progress будет автоматически рассчитан на backend на основе статуса
        // Для In Progress можно редактировать progress вручную
        if (status === 'In Progress' && progress !== undefined) {
          projectData.progress = progress;
        }
      }
      
      // Only include budget and assignments if user has permission
      if (canEditSensitive) {
        if (budget.trim()) {
          projectData.budget = parseFloat(budget);
        }
        if (taxRate.trim()) {
          projectData.taxRate = parseFloat(taxRate);
        }
        if (prepaymentType) {
          projectData.prepaymentType = prepaymentType as 'PERCENT' | 'FIXED';
        } else {
          projectData.prepaymentType = null;
        }
        if (prepaymentValue.trim()) {
          projectData.prepaymentValue = parseFloat(prepaymentValue);
        } else {
          projectData.prepaymentValue = null;
        }
        // Only send producer/manager/coordinator/finder/client if user can edit them
        // Use null instead of undefined to allow clearing values
        projectData.producerProfileId = producerId || null;
        projectData.managerProfileId = managerId || null;
        projectData.coordinatorProfileId = coordinatorId || null;
        projectData.finderProfileId = finderId || null;
        projectData.clientProfileId = clientId || null;
      }

      try {
      if (isEditing) {
          // Update project first
          await onUpdateProject({
          id: projectToEditId,
          ...projectData
          } as Project);
          
          // Then upload new image if selected
          if (imageFile && projectToEditId) {
            const uploadedImageUrl = await uploadProjectImage(projectToEditId, imageFile);
            if (uploadedImageUrl) {
              // Update project again with new image URL
              await onUpdateProject({
                id: projectToEditId,
                ...projectData,
                imageUrl: uploadedImageUrl
              } as Project);
            }
          }
          
          // Show success and close modal
          console.log('✅ Project update completed');
          resetForm();
          onClose();
      } else {
          // Create project first
          const createdProject = await onCreateProject(projectData);
          
          // Then upload image if selected
          if (imageFile && createdProject?.id) {
            const uploadedImageUrl = await uploadProjectImage(createdProject.id, imageFile);
            if (uploadedImageUrl) {
              // Update project with image URL
              await onUpdateProject({
                id: createdProject.id,
                ...projectData,
                imageUrl: uploadedImageUrl
              } as Project);
            }
          }
          
          // Create done → wizard step 2 (artists), do not switch to edit layout
          console.log('✅ Project creation completed, opening artists step');
          if (createdProject?.id) {
            setCreatedProjectId(createdProject.id);
            setWizardStep(2);
          } else {
            resetForm();
            onClose();
          }
      }
      } catch (error) {
        console.error('Error submitting project:', error);
        setFormError(error instanceof Error ? error.message : 'Ошибка при сохранении проекта');
      }
    } else {
      setFormError('Заполните обязательные поля: Название, Дата начала, Дата окончания');
    }
  };

  const handleEditClick = (project: Project) => {
    setProjectToEditId(project.id);
    setProjectName(project.name);
    setProjectStartDate(normalizeDate(project.startDate));
    setProjectEndDate(normalizeDate(project.endDate));
    setDescription(project.description || '');
    setStatus(project.status || 'Planning');
    setBudget(project.budget ? project.budget.toString() : '');
    setCurrency((project as any).currency || 'USD'); // Load currency from project
    setTaxRate(project.taxRate ? project.taxRate.toString() : '0');
    setPrepaymentType(project.prepaymentType || '');
    setPrepaymentValue(project.prepaymentValue ? project.prepaymentValue.toString() : '');
    setClient(project.client || '');
    setColor(project.color || '#6366f1');
    setPriority(project.priority || 'Medium');
    setProjectType(project.projectType || '');
    setTags(project.tags ? project.tags.join(', ') : '');
    setImageUrl(project.imageUrl || '');
    setProducerId(
      resolveAssignmentSelection(project.producerProfileId, project.producerId, managementUsers)
    );
    setManagerId(
      resolveAssignmentSelection(project.managerProfileId, project.managerId, managementUsers)
    );
    setCoordinatorId(
      resolveAssignmentSelection(
        project.coordinatorProfileId,
        project.coordinatorId,
        managementUsers
      )
    );
    setFinderId(
      resolveAssignmentSelection(project.finderProfileId, project.finderId, managementUsers)
    );
    setClientId(project.clientProfileId || project.clientId || '');
    setProgress(project.progress ?? 10);
    setMagicBoxId(project.magicBoxId || null);
    // Auto-set MagicBox status if project is linked
    if (project.magicBoxId || project.magicboxProjectId) {
      setStatus('MagicBox');
    }
    setFormError('');
  };

  const handleDelete = (project: Project) => {
    setDeleteConfirmation({
      isOpen: true,
      project: project
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.project) {
      onDeleteProject(deleteConfirmation.project.id);
      setDeleteConfirmation({ isOpen: false, project: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, project: null });
  };

  const finishCreateWizard = () => {
    resetForm();
    onClose();
  };

  // Status badge colors
  const getStatusColor = (status?: ProjectStatus): string => {
    switch (status) {
      case 'In Progress': return 'bg-blue-500';
      case 'Completed': return 'bg-indigo-500';
      case 'On Hold': return 'bg-yellow-500';
      case 'Cancelled': return 'bg-red-500';
      case 'MagicBox': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  // Priority badge colors
  const getPriorityColor = (priority?: ProjectPriority): string => {
    switch (priority) {
      case 'Urgent': return 'bg-red-600';
      case 'High': return 'bg-orange-500';
      case 'Medium': return 'bg-blue-500';
      case 'Low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={
        isEditing
          ? t('projects.editProject') || 'Редактировать проект'
          : wizardStep === 2
            ? t('projects.wizardStep2') || 'Художники'
            : t('projects.createProject') || 'Создать проект'
      }
      size={isEditing ? '2xl' : 'xl'}
    >
      {/* Create wizard step indicator */}
      {isCreateWizard && (
        <div className="flex gap-2 mb-4 border-b border-gray-600 pb-3">
          <button
            type="button"
            disabled={wizardStep === 1 || !!createdProjectId}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              wizardStep === 1
                ? 'bg-indigo-600/40 text-indigo-200 font-medium'
                : 'text-gray-400'
            }`}
          >
            1. {t('projects.wizardStep1') || 'Проект'}
          </button>
          <button
            type="button"
            disabled
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              wizardStep === 2
                ? 'bg-indigo-600/40 text-indigo-200 font-medium'
                : 'text-gray-500'
            }`}
          >
            2. {t('projects.wizardStep2') || 'Художники'}
          </button>
        </div>
      )}

      {/* Create wizard step 2: artists */}
      {isCreateWizard && wizardStep === 2 && createdProjectId && (
        <div className="space-y-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <ProjectArtistAssignment
              projectId={createdProjectId}
              currentRole={currentRole}
              onAssignmentsChange={() => {
                console.log('✅ Artist assignments updated');
              }}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-600">
            <Button type="button" onClick={finishCreateWizard} className="!bg-gray-600 hover:!bg-gray-500">
              {t('projects.wizardSkip') || 'Пропустить'}
            </Button>
            <Button type="button" onClick={finishCreateWizard}>
              {t('projects.wizardDone') || 'Готово'}
            </Button>
          </div>
        </div>
      )}

      {/* Create step 1 or Edit form */}
      {!(isCreateWizard && wizardStep === 2) && (
      <div className={isEditing ? 'grid grid-cols-1 lg:grid-cols-3 gap-6' : ''}>
        {/* Left Column: Create/Edit Project Form */}
        <div className={isEditing ? 'lg:col-span-2' : ''}>
          <form onSubmit={handleSubmit} className="space-y-3">
            {formError && (
              <div className="text-red-400 text-sm bg-red-900/20 p-3 rounded">
                {formError}
              </div>
            )}
            
            {/* Visual Section: Project Image & Name & Color */}
            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2">
                {/* Project Image Upload with Drag & Drop */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-300">
                    Изображение проекта
                  </label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                      const files = e.dataTransfer.files;
                      if (files && files[0]) {
                        const file = files[0];
                        if (file.type.startsWith('image/')) {
                          handleImageChange({ target: { files: [file] } } as any);
                        }
                      }
                    }}
                    className="border-2 border-dashed border-gray-600 rounded p-1.5 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-500/5 transition-colors"
                  >
                    {(imagePreview || imageUrl) ? (
                      <div className="relative h-[90px]">
                        <img
                          src={imagePreview || (imageUrl ? `/uploads/projects/${imageUrl}` : '')}
                          alt="Preview"
                          className="w-full h-full object-contain rounded"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-0.5 right-0.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5"
                        >
                          <ICONS.Trash className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="py-3 flex flex-col items-center justify-center h-[90px]">
                        <ICONS.Briefcase className="w-6 h-6 text-gray-500 mb-1" />
                        <p className="text-xs text-gray-400 mb-1">
                          Перетащите или
                        </p>
                        <label
                          htmlFor="project-image-upload"
                          className="inline-block px-2 py-0.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded cursor-pointer transition-colors"
                        >
                          Выберите
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="project-image-upload"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Project Name, Budget, and Color Picker */}
                <div className="space-y-2">
                  {/* Project Name */}
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-300">
                      Название проекта *
                    </label>
                    <input
                      type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Введите название проекта"
              required
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  {/* Currency - Available for all roles */}
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-300">
                      Валюта
                    </label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="RUB">RUB - Russian Ruble</option>
                      <option value="BYN">BYN - Belarusian Ruble</option>
                    </select>
                  </div>

                  {/* Budget & Tax - Only for Super Admin and Producer */}
                  {canEditSensitive && (
                    <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-300">
                        Бюджет
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={budget}
                          onChange={(e) => setBudget(e.target.value)}
                          placeholder="0.00"
                          className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <span className="text-gray-400 text-sm">{currency}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-300">
                          Налог (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={taxRate}
                          onChange={(e) => setTaxRate(e.target.value)}
                          placeholder="0"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-300">
                          {t('prepayment.title') || 'Предоплата'}
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={prepaymentType}
                            onChange={(e) => setPrepaymentType(e.target.value as 'PERCENT' | 'FIXED' | '')}
                            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">{t('prepayment.notSet') || 'Не задано'}</option>
                            <option value="PERCENT">{t('prepayment.percentOfBudget') || '% от бюджета'}</option>
                            <option value="FIXED">{t('prepayment.fixedAmount') || 'Фиксированная сумма'}</option>
                          </select>
                          {prepaymentType && (
                            <input
                              type="number"
                              min="0"
                              step={prepaymentType === 'PERCENT' ? '0.1' : '1'}
                              max={prepaymentType === 'PERCENT' ? '100' : undefined}
                              value={prepaymentValue}
                              onChange={(e) => setPrepaymentValue(e.target.value)}
                              placeholder={prepaymentType === 'PERCENT' ? (t('prepayment.placeholderPercent') || '0-100') : (t('prepayment.placeholderAmount') || 'Сумма')}
                              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
            <Input
                label="Дата начала *"
              type="date"
              value={projectStartDate}
              onChange={(e) => setProjectStartDate(e.target.value)}
              required
            />
            <Input
                label="Дата окончания *"
              type="date"
              value={projectEndDate}
              onChange={(e) => setProjectEndDate(e.target.value)}
              required
            />
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-300">
                Описание
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Краткое описание проекта"
                rows={2}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>

            {/* Статус, Приоритет и Прогресс на одной строке */}
            <div className="grid grid-cols-3 gap-2">
              <Select
                label="Статус"
                value={status}
                onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                disabled={status === 'MagicBox'}
              >
                <option value="Planning">{t('projectStatus.planning')}</option>
                <option value="In Progress">{t('projectStatus.inProgress')}</option>
                <option value="Completed">{t('projectStatus.completed')}</option>
                <option value="On Hold">{t('projectStatus.onHold')}</option>
                <option value="Cancelled">{t('projectStatus.cancelled')}</option>
                {status === 'MagicBox' && (
                  <option value="MagicBox">{t('projectStatus.magicbox')}</option>
                )}
              </Select>

              <Select
                label="Приоритет"
                value={priority}
                onChange={(e) => setPriority(e.target.value as ProjectPriority)}
              >
                <option value="Urgent">{t('priority.urgent')}</option>
                <option value="High">{t('priority.high')}</option>
                <option value="Medium">{t('priority.medium')}</option>
                <option value="Low">{t('priority.low')}</option>
              </Select>

              {/* Progress - только для проектов не в MagicBox */}
              {status !== 'MagicBox' && !magicBoxId ? (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-300">
                    Прогресс (%)
                    {status === 'Planning' && <span className="text-xs text-gray-500 ml-1">(10%)</span>}
                    {status === 'In Progress' && <span className="text-xs text-gray-500 ml-1">(50%)</span>}
                    {status === 'Completed' && <span className="text-xs text-gray-500 ml-1">(100%)</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={(e) => setProgress(parseInt(e.target.value) || 0)}
                    disabled={status === 'Planning' || status === 'In Progress' || status === 'Completed' || status === 'On Hold' || status === 'Cancelled'}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-300">
                    Прогресс (%)
                  </label>
                  <div className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-400 text-sm">
                    🔗 Синхронизируется с LightBox
                  </div>
                </div>
              )}
            </div>

            {magicBoxId && (
              <div className="bg-blue-900/20 border border-blue-500 rounded-md p-2">
                <p className="text-xs text-blue-300">
                  🔗 Проект синхронизирован с LightBox. Прогресс обновляется автоматически через API.
                </p>
              </div>
            )}

            {/* Клиент и Тип проекта на одной строке */}
            <div className="grid grid-cols-2 gap-2">
            <Input
              label="Клиент"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="Название клиента/студии"
            />

            <Select
                label={t('projects.projectType') || 'Тип проекта'}
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as ProjectType)}
            >
                <option value="">{t('projects.notSelected') || 'Не выбран'}</option>
                <option value="Feature Film">{t('projectTypes.featureFilm') || 'Feature Film'}</option>
                <option value="TV Series">{t('projectTypes.tvSeries') || 'TV Series'}</option>
                <option value="Commercial">{t('projectTypes.commercial') || 'Commercial'}</option>
                <option value="Music Video">{t('projectTypes.musicVideo') || 'Music Video'}</option>
                <option value="Game Cinematic">{t('projectTypes.gameCinematic') || 'Game Cinematic'}</option>
                <option value="VR/AR">{t('projectTypes.vrAr') || 'VR/AR'}</option>
                <option value="Other">{t('projectTypes.other') || 'Other'}</option>
            </Select>
            </div>

            <Input
              label="Теги"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Теги через запятую (vfx, action, drama)"
            />
            
            {/* User Assignments - Only for Super Admin and Producer */}
            {canEditSensitive && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    label="Producer"
                    value={producerId}
                    onChange={(e) => setProducerId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Не назначен</option>
                    {users
                      .filter(user =>
                        (PRODUCER_ASSIGNABLE_TYPES as readonly string[]).includes(user.role)
                      )
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.role})
                        </option>
                      ))
                    }
                  </Select>
                  
                  <Select
                    label="PM / Supervisor"
                    value={managerId}
                    onChange={(e) => setManagerId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Не назначен</option>
                    {users
                      .filter(user =>
                        ['supervisor', 'admin', 'coordinator'].includes(user.role)
                      )
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.role})
                        </option>
                      ))
                    }
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    label="Coordinator"
                    value={coordinatorId}
                    onChange={(e) => setCoordinatorId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Не назначен</option>
                    {users
                      .filter(user =>
                        (COORDINATOR_ASSIGNABLE_TYPES as readonly string[]).includes(user.role)
                      )
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.role})
                        </option>
                      ))
                    }
                  </Select>
                
                  <Select
                    label="Finder"
                    value={finderId}
                    onChange={(e) => setFinderId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Не назначен</option>
                    {users
                      .filter(user =>
                        ['finder', 'producer', 'sales', 'admin'].includes(user.role)
                      )
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.role})
                        </option>
                      ))}
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    label="Client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    disabled={loadingClients}
                  >
                    <option value="">Не назначен</option>
                    {clients.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username || user.email}
                      </option>
                    ))}
                  </Select>
                </div>
              </>
            )}
            
            <div className="flex space-x-3 pt-1">
              <Button type="submit" className="flex-1">
                {isEditing
                  ? (t('common.save') || 'Обновить')
                  : (t('projects.createAndContinue') || 'Создать и далее')}
              </Button>
              <Button type="button" onClick={handleModalClose} className="!bg-gray-600 hover:!bg-gray-500">
                {t('common.cancel') || 'Отмена'}
              </Button>
            </div>
          </form>
        </div>

        {/* Right Column: Assigned Artists (edit only) */}
        {isEditing && (
          <div className="lg:col-span-1 bg-gray-800/50 rounded-lg p-4">
            <ProjectArtistAssignment
              projectId={projectToEditId}
              currentRole={currentRole}
              onAssignmentsChange={() => {
                console.log('✅ Artist assignments updated');
              }}
            />
          </div>
        )}
      </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        title="Подтвердите удаление"
        message={`Вы уверены, что хотите удалить проект "${deleteConfirmation.project?.name}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
      />
    </Modal>
  );
};

export default ProjectManagementModal;

